import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DOCUMENT } from '@angular/common';

// Models
import { FormValidator } from './validators/form-validator';
import { ItemVariation } from '../shared/models/item-variation.model';
import { Item } from '../shared/models/item.model';

// Components
import { PaymentPlanTextComponent } from 'src/sale-item/payment-plan-text/payment-plan-text.component';

// Services
import { AmountFormatterService } from './amount-formatter.service';
import { ItemService } from 'src/shared/services/item.service';
import { PaymentAmountOptionsComponent } from './payment-amount-options/payment-amount-options.component';
import { PriceSchedule } from 'src/shared/models/price-schedule.model';
import { SeFeThemesLoader } from 'se-fe-themes';
import { SeFeFormFieldConfig } from 'se-fe-form-field';

@Component({
  selector: 'sale-item-element',
  templateUrl: './sale-item.component.html',
  styleUrls: ['./sale-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SaleItemComponent implements OnChanges, OnInit {
  public nameFieldConfig: SeFeFormFieldConfig = {
    label: 'Name',
    errorMessages: {
      required: 'Enter sale item name',
      maxlength: 'Name cannot exceed 64 characters',
      pattern: 'Name cannot contain colons (:), semicolons (;), or backslashes (\\)',
    },
  };
  @Output() closeSaleItemModal = new EventEmitter<any>();
  @Output() success = new EventEmitter<Item>();
  @Input() originalItem?: Item;
  @Input() organizationId: string;
  @Input() showSaleItemType?: string;
  @Input('showInfoAlert')
  get showInfoAlert(): boolean {
    return this._showInfoAlert;
  }
  set showInfoAlert(value: boolean) {
    this._showInfoAlert = '' + value !== 'false';
  }
  @Input('showTaxCode')
  get showTaxCode(): boolean {
    return this._showTaxCode;
  }
  set showTaxCode(value: boolean) {
    this._showTaxCode = '' + value !== 'false';
  }

  _showInfoAlert = false;
  _showTaxCode = false;
  item: Item;
  editable: boolean;
  errorAction: string;
  formValidator: FormValidator;
  itemVariation = new ItemVariation({});
  openAnotherModalCheckbox = false;
  reopen = false;
  serverErrors: string[] = [];
  submitting = false;
  submitTitle: string;
  title: string;
  itemForm: FormGroup;

  @ViewChild('template', {static: true}) template: TemplateRef<any>;
  @ViewChild(PaymentAmountOptionsComponent) paymentAmountOptions: PaymentAmountOptionsComponent;
  @ViewChild(PaymentPlanTextComponent) paymentPlanTextComponent: PaymentPlanTextComponent;

  constructor(
    private elementRef: ElementRef,
    private detector: ChangeDetectorRef,
    private formBuilder: FormBuilder,
    private itemService: ItemService,
    private viewContainerRef: ViewContainerRef,
    private amountFormatter: AmountFormatterService,
    @Inject(DOCUMENT) private document: Document
  ) {
    SeFeThemesLoader.load({
      supportedThemes: 'light',
      url: 'https://se-fe-angular-services.ui.sportngin.com'
    });

    this.itemForm = new FormGroup({
      name: new FormControl('', [Validators.required, Validators.maxLength(64), Validators.pattern(/^[^:;\\]*$/)]),
    });
  }

  ngOnInit() {
    const svgEvent = new Event('se-financial-admin-load-svg', { bubbles: true });
    this.elementRef.nativeElement.dispatchEvent(svgEvent);
    this.viewContainerRef.createEmbeddedView(this.template);
    this.detector.detectChanges();
    if (!this.editable) {
      this.addTitles();
      this.item = new Item({});
    }
    this.setupForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.item = new Item({ ...this.originalItem });
    this.editable = this.item && !!this.item.id;
    if (this.editable) {
      this.editTitles();
      // @ts-ignore
      this.itemVariation = { ...this.originalItem.variations[0] };
      this.setupForm();
    }
  }

  private nameControl(): AbstractControl {
    return this.itemForm.get('name');
  }

  public submit(): void {
    this.paymentAmountOptions.preSubmitCheck();
    this.nameControl().markAsDirty();
    if (this.formValidator.isValid() && this.priceVariationsAreValid() && !this.submitting) {
      this.submitting = true;
      this.updateItemVariation(this.itemForm.value);
      this.item.name = this.itemVariation.name;
      this.item.variations = [this.itemVariation];
      this.item.variations[0].price_schedules = this.buildPriceSchedules();
      const params = { organization_id: this.organizationId };
      this.callApi(params);
    }
  }

  private priceVariationsAreValid(): boolean {
    let priceVariationsAreValid = true;
    this.paymentAmountOptions.priceVariationComponents?.forEach((component) => {
      const formGroup = component.formGroup;
      Object.keys(formGroup.controls).forEach(key => {
        const control = formGroup.get(key);
        control.markAsTouched();
        control.markAsDirty();
      });
      this.touchDatePickers();
      if (formGroup.invalid) {
        priceVariationsAreValid = false;
      }
    });
    return priceVariationsAreValid;
  }

  private touchDatePickers(): void {
    const datePickersFormControlsNames = ['start_date', 'end_date'];
    datePickersFormControlsNames.forEach((name) => {
      const datePickers = this.document.querySelectorAll(`[formcontrolname="${name}"] input`) as NodeListOf<HTMLInputElement>;
      datePickers.forEach(datePicker => {
        datePicker.focus();
        datePicker.blur();
      });
    });
  }

  private buildPriceSchedules(): Partial<PriceSchedule>[] {
    return this.paymentAmountOptions.priceVariationComponents?.map((component) => {
      const startDateChanged = component.dateUpdated(component.dateType.START);
      const endDateChanged = component.dateUpdated(component.dateType.END);
      const formValue = component.formGroup.value;
      const priceCents = Math.round(parseFloat(formValue.price_cents.replace(/,/g, '')) * 100);
      const priceSchedule = {
        pricing_name: formValue.pricing_name,
        price_cents: priceCents,
        upfront_amount_cents: this.priceScheduleUpfrontAmount(priceCents, formValue),
        price_schedule_id: component.priceVariation.price_schedule_id,
      };

      if (startDateChanged) {
        priceSchedule['start_date'] = new Date(formValue.start_date).toISOString();
      }
      if (endDateChanged) {
        priceSchedule['end_date'] = new Date(formValue.end_date).toISOString();
      }

      return priceSchedule;
    });
  }

  private priceScheduleUpfrontAmount(priceCents: number, formValue): number {
    if (this.paymentAmountOptions.isFullAmount()) {
      return priceCents;
    } else if (this.paymentAmountOptions.isZeroAmount()) {
      return 0;
    } else if (this.paymentAmountOptions.isPartialAmount()) {
      return Math.round(parseFloat(formValue.upfront_amount_cents.replace(/,/g, '')) * 100);
    }
  }

  private updateItemVariation(value): void {
    this.itemVariation.price = value.price;
    this.itemVariation.price_cents = Math.round(this.amountFormatter.sanitizePrice(value.price) * 100);
    this.itemVariation.name = value.name;
    this.itemVariation.sku = value.sku;
    this.itemVariation.tax_code = value.taxCode;
    this.itemVariation.zero_rate_type = value.zeroRateType;
    this.itemVariation.tax_integration_attributes = { tax_code: this.itemVariation.tax_code };
    this.itemVariation.reference_code = this.paymentAmountOptions.referenceCode;
    this.paymentAmountOptions.setUpfrontAmount();
    this.paymentAmountOptions.setAdditionalSettings();
  }

  private createItem(item: Item, params: any): void {
    this.itemService.createItem(item, params)
      .subscribe(
        response => this.onItemCreateSuccess(response.result),
        response => this.onItemResponseError(response.error),
        () => { this.submitting = false; }
      );
  }

  private editItem(params: any): void {
    this.itemService.editItem(this.item, params)
      .subscribe(
        () => this.onEditItemSuccess(),
        response => this.onItemResponseError(response.error),
        () => { this.submitting = false; }
      );
  }

  private onItemCreateSuccess(result: Item): void {
    this.reopen = true;
    this.success.emit(result);
    this.resetModal();
    if (!this.openAnotherModalCheckbox) {
      this.closeModal();
    }
  }

  private onEditItemSuccess(): void {
    this.success.emit(this.item);
    this.closeModal();
  }

  private onItemResponseError(error: any): void {
    this.submitting = false;
    this.serverErrors = error.error.messages;
  }

  public closeModal(): void {
    this.openAnotherModalCheckbox = false;
    this.resetModal();
    this.closeSaleItemModal.emit(true);
  }

  private resetModal(): void {
    if (this.editable) {
      this.item = new Item({ ...this.originalItem });
      this.itemVariation = new ItemVariation({ ...this.originalItem.variations[0] });
      this.setupForm();
    }
    this.paymentAmountOptions.resetPaymentAmountOptions();
    this.detector.detectChanges();
    this.serverErrors = [];
  }

  public toggleAddAnother(): void {
    this.openAnotherModalCheckbox = !this.openAnotherModalCheckbox;
  }

  public trimName(): void {
    if (this.nameControl().value) {
      this.nameControl().setValue(this.itemForm.value.name.trim());
    }
  }

  public getTrimmedNameLength(): number {
    if (this.nameControl().value) {
      return this.nameControl().value.trim().length;
    }
    return 0;
  }

  private editTitles(): void {
    this.submitTitle = 'Save';
    this.title = 'Edit Sale Item';
    this.errorAction = 'edited';
  }

  private addTitles(): void {
    this.submitTitle = 'Add';
    this.title = 'Add Sale Item';
    this.errorAction = 'added';
  }

  private callApi(params): void {
    this.editable ? this.editItem(params) : this.createItem(this.item, params);
  }

  private setupForm(): void {
    this.itemForm.get('name').setValue(this.item.name);
    this.formValidator = new FormValidator(this.itemForm);
  }
}
